/**
 * ImprovedModal - Resizable, scrollable modal component
 * 
 * CHANGE HISTORY:
 * - 251201_0500 danielbeer: Created improved modal with resize and scroll
 */

import React, { useState, useRef, useEffect } from 'react';
import './ImprovedModal.css';

export interface ImprovedModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  initialWidth?: number;
  initialHeight?: number;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
}

export function ImprovedModal({
  isOpen,
  onClose,
  title,
  children,
  initialWidth = 800,
  initialHeight = 600,
  minWidth = 400,
  minHeight = 300,
  maxWidth = 1400,
  maxHeight = 900
}: ImprovedModalProps) {
  const [size, setSize] = useState({ width: initialWidth, height: initialHeight });
  const [isResizing, setIsResizing] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });

  useEffect(() => {
    if (!isOpen) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const deltaX = e.clientX - resizeStartRef.current.x;
      const deltaY = e.clientY - resizeStartRef.current.y;

      const newWidth = Math.min(
        Math.max(resizeStartRef.current.width + deltaX, minWidth),
        maxWidth
      );
      const newHeight = Math.min(
        Math.max(resizeStartRef.current.height + deltaY, minHeight),
        maxHeight
      );

      setSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, minWidth, minHeight, maxWidth, maxHeight]);

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height
    };
  };

  if (!isOpen) return null;

  return (
    <div className="improved-modal-overlay" onClick={onClose}>
      <div
        ref={modalRef}
        className="improved-modal"
        style={{ width: size.width, height: size.height }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="improved-modal-header">
          <h3 className="improved-modal-title">{title}</h3>
          <button
            onClick={onClose}
            className="icon-btn improved-modal-close"
            title="Close"
          >
            ✕
          </button>
        </div>

        {/* Content with scroll */}
        <div className="improved-modal-content">
          {children}
        </div>

        {/* Resize Handle */}
        <div
          className="improved-modal-resize-handle"
          onMouseDown={handleResizeStart}
          title="Drag to resize"
        >
          ⋰
        </div>
      </div>
    </div>
  );
}
